import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { VacationRequestWithRelations } from '@api-swagger-client/index';
import { TemporalLinkService } from '@app/@shared/temporal-link.service';
import {
    StatusTone,
    fullName,
    initials,
    statusTone,
} from '@app/@shared/approval-detail/approval-detail.utils';
import { LinkExpirationModule } from '@app/@shared/link-expiration/link-expiration.module';
import { VacationsService } from '@app/client/vacations/services/vacations.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { forkJoin, timer } from 'rxjs';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-vacation-request-authorization',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,

        // PrimeNG Modules
        TableModule,
        ButtonModule,
        InputTextModule,
        ToastModule,
        ToolbarModule,
        DialogModule,
        DropdownModule,
        CalendarModule,
        MultiSelectModule,
        CheckboxModule,
        CardModule,
        PanelModule,
        DividerModule,
        TabViewModule,
        ChipModule,
        TagModule,
        AvatarModule,
        ProgressSpinnerModule,
        TooltipModule,
        RippleModule,
        ConfirmDialogModule,
        InputTextareaModule,
        RadioButtonModule,
        InputNumberModule,
        BadgeModule,
        TimelineModule,
        FieldsetModule,
        MessagesModule,
        MessageModule,
        OverlayPanelModule,
        SkeletonModule,
        LinkExpirationModule,
    ],
    providers: [MessageService, DialogService],
    templateUrl: './vacation-request-authorization.component.html',
    styleUrl: './vacation-request-authorization.component.scss',
})
export class VacationRequestAuthorizationComponent {
    token: string | null = null;
    referenceId: string | null = null;
    loading: boolean = true;
    error: string | null = null;
    success: boolean = false;
    verifying: boolean = false;

    expirationDate: Date | null = null;
    linkExpired: boolean = false;
    linkDetails: any = null;

    vacationRequest: VacationRequestWithRelations | null = null;
    processingAction = false;

    // Dialogs
    submitDialogVisible = false;
    approveDialogVisible = false;
    rejectDialogVisible = false;
    commentText = '';
    commentError = false;

    canProcessRequest: boolean = false;
    actionComments: string = '';
    showReasonError: boolean = false;
    showRejectMode: boolean = false;
    selectedRequest: any | null = null;
    constructor(
        private activatedRoute: ActivatedRoute,
        private temporalLinkService: TemporalLinkService,
        private messageService: MessageService,
        private vacationsService: VacationsService,
    ) {}

    ngOnInit(): void {
        // Extraer los parámetros de la URL
        this.activatedRoute.queryParams.subscribe((params) => {
            this.token = params['token'];
            this.referenceId = params['referenceId'];
            if (this.token) {
                this.validateLink();
            } else {
                this.error = 'Enlace inválido: token no encontrado';
                this.loading = false;
            }
        });
    }

    validateLink(): void {
        if (!this.token) {
            this.error = 'Token no proporcionado';
            this.loading = false;
            return;
        }

        this.temporalLinkService.validateTemporalLink(this.token).subscribe({
            next: (response) => {
                if (response && response.valid) {
                    // Marcamos como exitoso (el link es válido)
                    this.success = true;
                    this.linkDetails = response.link;

                    if (this.linkDetails.expirationDate) {
                        this.expirationDate = new Date(this.linkDetails.expirationDate);
                        const now = new Date();
                        if (this.expirationDate < now) {
                            this.linkExpired = true;
                            this.showToast('warning', 'Enlace expirado', 'El enlace de autorización ha expirado. Solicita uno nuevo al administrador.');
                        }
                    }

                    if (!(response.link as any)?.used) {
                        this.getVacationRequestDetail();
                    }

                    if (this.linkDetails.used) {
                        timer(1000).subscribe(() => {
                            this.showToast('warn', 'Enlace ya utilizado', 'Este enlace de autorización es de un solo uso y ya fue utilizado anteriormente.');
                        });
                    }
                } else {
                    this.linkExpired = true;
                    this.error = response.message || 'El enlace de verificación no es válido';
                    this.showToast('error', 'Enlace inválido', this.error);
                }
                this.loading = false;
            },
            error: (err) => {
                this.error = err.message || 'Error al verificar el enlace';
                this.loading = false;
                console.error('Error al verificar el enlace:', err);
                timer(500).subscribe(() => {
                    this.showToast('error', 'Error de verificación', err.error?.message || 'Ha ocurrido un error al verificar el enlace');
                });
            },
        });
    }

    onExpirationStatusChanged(expired: boolean): void {
        this.linkExpired = expired;
        if (expired) {
            this.showToast('warning', 'Enlace expirado', 'El tiempo de autorización ha finalizado. Este enlace es de un solo uso.');
        }
    }

    showToast(severity: string = 'info', summary: string = '', detail: string = ''): void {
        this.messageService.add({ key: 'tst', severity, summary, detail });
    }

    private getVacationRequestDetail(): void {
        this.loading = true;

        forkJoin({
            request: this.vacationsService.findByIdFromLink(
                this.referenceId!,
                this.token!,
            ),
            canProcess: this.vacationsService.canProcessFromLink(
                this.referenceId!,
                this.token!,
            ),
        }).subscribe({
            next: ({ request, canProcess }) => {
                this.selectedRequest = request['data'];

                if (canProcess.success) {
                    this.canProcessRequest = canProcess.data;
                }

                this.loading = false;
            },
            error: () => {
                this.showToast('error', 'Error', 'No se pudo cargar la información de la solicitud');
                this.loading = false;
            },
        });
    }

    /**
     * Días que la solicitud toma a cuenta del próximo período
     */
    getAdvanceDays(request: any): number {
        return request?.daysInNextPeriod ?? request?.advanceDays ?? 0;
    }

    getStatusTone(status: string): StatusTone {
        return statusTone(status);
    }

    fullName = fullName;
    initials = initials;

    /**
     * Obtener etiqueta del estado
     */
    getStatusLabel(status: string): string {
        const statusLabels: { [key: string]: string } = {
            draft: 'Borrador',
            pending: 'Pendiente de aprobación',
            pending_payroll: 'Pendiente de nómina',
            approved_by_boss: 'Aprobada por el jefe · pendiente de nómina',
            approved_by_payroll: 'Aprobada por nómina',
            approved: 'Aprobada',
            rejected: 'Rechazada',
            cancelled: 'Cancelada',
        };
        return statusLabels[status] || status;
    }

    /**
     * Formatear fecha
     * Para fechas en formato string "YYYY-MM-DD" se parsean manualmente
     * para evitar problemas de zona horaria
     */
    formatDate(date: string | Date): string {
        if (!date) return '-';

        let d: Date;

        // Si es un string en formato "YYYY-MM-DD" (sin hora), parsearlo manualmente
        // para evitar que JavaScript lo interprete como UTC
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split('-').map(Number);
            d = new Date(year, month - 1, day); // month es 0-indexed
        } else {
            d = new Date(date);
        }

        return d.toLocaleDateString('es-MX');
    }
    /**
     * Verificar si una solicitud puede ser enviada
     */
    canSubmit(request: any): boolean {
        return request.status === 'draft';
    }

    /**
     * Verificar si una solicitud puede ser cancelada
     */
    canCancel(request: any): boolean {
        return (
            request.status === 'pending' ||
            /*        request.status === 'pending_hr' || */
            request.status === 'approved_by_boss' ||
            request.status === 'approved'
        );
    }

    /**
     * Verificar si una solicitud puede ser aprobada
     */
    canApprove(request: any): boolean {
        return (
            (request.status === 'pending' ||
                request.status === 'pending_payroll') &&
            this.canProcessRequest
        );
    }

    /**
     * Verificar si una solicitud puede ser rechazada
     */
    canReject(request: any): boolean {
        return (
            (request.status === 'pending' ||
                request.status === 'pending_payroll') &&
            this.canProcessRequest
        );
    }

     /**
     * Confirmar aprobación
     */
    confirmApprove() {
        if (!this.selectedRequest) return;

        this.processingAction = true;

        const payload: any = {};
        if (this.actionComments && this.actionComments.trim() !== '') {
            payload.comments = this.actionComments.trim();
        }

        this.vacationsService
            .approveVacationRequestFromLink(this.selectedRequest.id, this.token!, payload)
            .subscribe({
                next: (response) => {
                    this.processingAction = false;
                    if (response.success) {
                        this.showToast('success', 'Aprobada', 'La solicitud ha sido aprobada exitosamente');
                        this.ngOnInit();
                    } else {
                        this.showToast('error', 'Error', response.message || 'No se pudo aprobar la solicitud');
                    }
                },
                error: (error) => {
                    this.processingAction = false;
                    this.showToast('error', 'Error', error.error?.message || 'No se pudo aprobar la solicitud');
                },
            });
    }

    /**
     * Alternar modo de rechazo
     */
    toggleRejectMode() {
        this.showRejectMode = !this.showRejectMode;
        this.showReasonError = false;
    }

    /**
     * Confirmar rechazo
     */
    confirmReject() {
        if (!this.selectedRequest) return;

        if (!this.actionComments || this.actionComments.trim() === '') {
            this.showReasonError = true;
            this.showToast('warn', 'Campo requerido', 'Debes especificar la razón del rechazo');
            return;
        }

        this.processingAction = true;
        this.showReasonError = false;

        const payload = {
            rejectionReason: this.actionComments.trim(),
        };

        this.vacationsService
            .rejectVacationRequestFromLink(this.selectedRequest.id, this.token!, payload)
            .subscribe({
                next: (response) => {
                    this.processingAction = false;
                    if (response.success) {
                        this.showToast('success', 'Rechazada', 'La solicitud ha sido rechazada');
                        this.ngOnInit();
                    } else {
                        this.showToast('error', 'Error', response.message || 'No se pudo rechazar la solicitud');
                    }
                },
                error: (error) => {
                    this.processingAction = false;
                    this.showToast('error', 'Error', error.error?.message || 'No se pudo rechazar la solicitud');
                },
            });
    }

    submitRequest(_request: any): void {
        this.messageService.add({
            severity: 'warn',
            summary: 'No disponible',
            detail: 'El envío de solicitudes no está disponible desde este enlace',
        });
    }
}
